/**
 * Einstellungen → „Prompt für Claude Code“: die Grundregeln, die in jedem
 * Auftrag oben stehen, und die Vorlagen, aus denen man beim Erzeugen wählt
 * („Rückmeldungen einarbeiten“, „Nur prüfen“ …). Der Auftrag selbst entsteht
 * in der Mail-Werkstatt (ClaudePromptDialog); hier wird nur gepflegt.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowDown, ArrowUp, Eye, EyeOff, Pencil, Plus, RotateCcw, Star, Terminal, Trash2 } from 'lucide-react';
import { Modal } from '../../components/Modal/Modal';
import { useToast } from '../../components/Toast';
import {
  aendereVorlage, ladeGrundregeln, ladeVorlagen, legeVorlageAn, loescheVorlage, ordneVorlagen, setzeAktiv, setzeStandard,
  speichereGrundregeln, type VorlageEingabe,
} from '../../lib/promptVorlagen';
import {
  GRUNDREGELN_MAX, STANDARD_GRUNDREGELN, VORLAGE_ANWEISUNGEN_MAX, VORLAGE_BESCHREIBUNG_MAX, VORLAGE_NAME_MAX,
  type PromptVorlage,
} from '../../lib/promptStandard';
import './PromptTab.css';

const LEER: VorlageEingabe = { name: '', beschreibung: '', anweisungen: '', aendert_mail: true };

function fehlerText(e: unknown): string {
  return e instanceof Error && e.message ? e.message : 'Das hat nicht geklappt. Versuch es bitte noch einmal.';
}

export function PromptTab() {
  const qc = useQueryClient();
  const toast = useToast();
  const vorlagen = useQuery({ queryKey: ['prompt-vorlagen'], queryFn: ladeVorlagen });
  const grundregeln = useQuery({ queryKey: ['prompt-grundregeln'], queryFn: ladeGrundregeln });

  const [entwurf, setEntwurf] = useState<string | null>(null);
  const [dialog, setDialog] = useState<{ id: string | null; daten: VorlageEingabe } | null>(null);

  const liste = vorlagen.data ?? [];
  const gespeichert = grundregeln.data ?? STANDARD_GRUNDREGELN;
  const text = entwurf ?? gespeichert;
  const geaendert = entwurf !== null && entwurf.trim() !== gespeichert.trim();

  const neuLaden = () => qc.invalidateQueries({ queryKey: ['prompt-vorlagen'] });
  const melden = (erfolg: string) => ({
    onSuccess: () => { toast.success(erfolg); void neuLaden(); },
    onError: (e: unknown) => toast.error(fehlerText(e)),
  });

  const regelnSpeichern = useMutation({
    mutationFn: (t: string) => speichereGrundregeln(t),
    onSuccess: () => { toast.success('Grundregeln gespeichert.'); setEntwurf(null); void qc.invalidateQueries({ queryKey: ['prompt-grundregeln'] }); },
    onError: (e: unknown) => toast.error(fehlerText(e)),
  });
  const speichern = useMutation({
    mutationFn: ({ id, daten }: { id: string | null; daten: VorlageEingabe }) => (
      id ? aendereVorlage(id, daten) : legeVorlageAn(daten, ((liste[liste.length - 1]?.sortierung ?? 0) + 10))
    ),
    onSuccess: (_r, v) => { toast.success(v.id ? 'Vorlage gespeichert.' : 'Vorlage angelegt.'); setDialog(null); void neuLaden(); },
    onError: (e: unknown) => toast.error(fehlerText(e)),
  });
  const standard = useMutation({ mutationFn: setzeStandard, ...melden('Ist jetzt die Standardvorlage.') });
  const aktiv = useMutation({ mutationFn: ({ id, an }: { id: string; an: boolean }) => setzeAktiv(id, an), ...melden('Gespeichert.') });
  const loeschen = useMutation({ mutationFn: loescheVorlage, ...melden('Vorlage gelöscht.') });
  const ordnen = useMutation({ mutationFn: ordneVorlagen, ...melden('Reihenfolge gespeichert.') });

  const bewegen = (i: number, richtung: -1 | 1) => {
    const neu = [...liste];
    [neu[i], neu[i + richtung]] = [neu[i + richtung], neu[i]];
    ordnen.mutate(neu);
  };

  const dialogGueltig = !!dialog && dialog.daten.name.trim() !== '' && dialog.daten.anweisungen.trim() !== '';

  return (
    <div className="pt">
      <div className="card pt-karte">
        <h2 className="card-title"><Terminal size={16} aria-hidden="true" /> So entsteht der Auftrag</h2>
        <p className="pt-text">
          In der Mail-Werkstatt erzeugen Admins aus den Rückmeldungen einer Mail einen Arbeitsauftrag für Claude Code.
          Oben stehen immer die <strong>Grundregeln</strong>. Danach folgt der Text der gewählten <strong>Vorlage</strong> —
          sie bestimmt, ob Claude Code die Mail ändert oder nur prüft.
        </p>
      </div>

      <div className="card pt-karte">
        <h2 className="card-title">Grundregeln</h2>
        <p className="pt-text">Stehen in jedem Auftrag ganz oben, egal welche Vorlage gewählt ist. Eine Regel pro Zeile, mit „- “ davor.</p>
        {grundregeln.isLoading ? (
          <div className="loading-spinner" style={{ margin: '1rem auto' }} />
        ) : (
          <>
            <textarea
              className="textarea pt-regeln"
              rows={7}
              maxLength={GRUNDREGELN_MAX}
              value={text}
              onChange={(e) => setEntwurf(e.target.value)}
              aria-label="Grundregeln"
            />
            <div className="pt-zeile">
              <span className="pt-zaehler">{text.length.toLocaleString('de-DE')} / {GRUNDREGELN_MAX.toLocaleString('de-DE')} Zeichen</span>
              <div className="pt-knoepfe">
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  onClick={() => setEntwurf(STANDARD_GRUNDREGELN)}
                  disabled={text.trim() === STANDARD_GRUNDREGELN.trim()}
                  title="Die ursprünglichen vier Regeln einsetzen (gespeichert wird erst mit „Speichern“)"
                >
                  <RotateCcw size={14} aria-hidden="true" /> Vorschlag einsetzen
                </button>
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  onClick={() => regelnSpeichern.mutate(text)}
                  disabled={!geaendert || text.trim() === '' || regelnSpeichern.isPending}
                >
                  Speichern
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      <div className="card pt-karte">
        <div className="pt-kopf">
          <h2 className="card-title">Vorlagen</h2>
          <button type="button" className="btn btn-primary btn-sm" onClick={() => setDialog({ id: null, daten: LEER })}>
            <Plus size={14} aria-hidden="true" /> Neue Vorlage
          </button>
        </div>
        <p className="pt-text">
          Beim Erzeugen wählt man unter „Was soll passieren?“; vorgewählt ist die Standardvorlage. Ausgeblendete Vorlagen
          erscheinen dort nicht, bleiben hier aber erhalten.
        </p>

        {vorlagen.isLoading ? (
          <div className="loading-spinner" style={{ margin: '1rem auto' }} />
        ) : vorlagen.isError ? (
          <div className="alert alert-warning" role="alert">{fehlerText(vorlagen.error)} Bis dahin gilt der eingebaute Standardtext.</div>
        ) : liste.length === 0 ? (
          <div className="alert alert-info">Noch keine Vorlage angelegt — im Auftrag gilt der eingebaute Standardtext „Rückmeldungen einarbeiten“.</div>
        ) : (
          <ul className="pt-liste">
            {liste.map((v, i) => (
              <VorlageZeile
                key={v.id}
                v={v}
                erste={i === 0}
                letzte={i === liste.length - 1}
                beschaeftigt={ordnen.isPending || standard.isPending || aktiv.isPending || loeschen.isPending}
                onHoch={() => bewegen(i, -1)}
                onRunter={() => bewegen(i, 1)}
                onBearbeiten={() => setDialog({ id: v.id, daten: { name: v.name, beschreibung: v.beschreibung ?? '', anweisungen: v.anweisungen, aendert_mail: v.aendert_mail } })}
                onStandard={() => standard.mutate(v.id)}
                onAktiv={() => aktiv.mutate({ id: v.id, an: !v.aktiv })}
                onLoeschen={() => { if (confirm(`Vorlage „${v.name}“ wirklich löschen?`)) loeschen.mutate(v.id); }}
              />
            ))}
          </ul>
        )}
      </div>

      <Modal isOpen={dialog !== null} onClose={() => setDialog(null)} title={dialog?.id ? 'Vorlage bearbeiten' : 'Neue Vorlage'} size="lg">
        {dialog && (
          <>
            <div className="form-group">
              <label className="form-label" htmlFor="pt-name">Name *</label>
              <input
                id="pt-name"
                className="form-input"
                maxLength={VORLAGE_NAME_MAX}
                placeholder="z. B. Nur prüfen, nichts ändern"
                value={dialog.daten.name}
                onChange={(e) => setDialog({ ...dialog, daten: { ...dialog.daten, name: e.target.value } })}
              />
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="pt-besch">Kurze Beschreibung</label>
              <input
                id="pt-besch"
                className="form-input"
                maxLength={VORLAGE_BESCHREIBUNG_MAX}
                placeholder="Erscheint im Auftrag unter der Auswahl"
                value={dialog.daten.beschreibung ?? ''}
                onChange={(e) => setDialog({ ...dialog, daten: { ...dialog.daten, beschreibung: e.target.value } })}
              />
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="pt-text">Text im Auftrag („Deine Aufgabe“) *</label>
              <textarea
                id="pt-text"
                className="textarea pt-anweisungen"
                rows={12}
                maxLength={VORLAGE_ANWEISUNGEN_MAX}
                value={dialog.daten.anweisungen}
                onChange={(e) => setDialog({ ...dialog, daten: { ...dialog.daten, anweisungen: e.target.value } })}
              />
              <span className="pt-zaehler">{dialog.daten.anweisungen.length.toLocaleString('de-DE')} / {VORLAGE_ANWEISUNGEN_MAX.toLocaleString('de-DE')} Zeichen</span>
            </div>
            <div className="form-group">
              <label className="checkbox-row">
                <input
                  type="checkbox"
                  checked={dialog.daten.aendert_mail}
                  onChange={(e) => setDialog({ ...dialog, daten: { ...dialog.daten, aendert_mail: e.target.checked } })}
                />
                <span>Der Auftrag ändert die Mail</span>
              </label>
              <p className="pt-hilfe">
                Mit Haken hängt der Auftrag das Vorgehen an (Mail per SQL ändern, nach Brevo übertragen) und bittet am Ende um
                eine Antwort an die Rückmeldenden. Ohne Haken wird nur geprüft oder gelesen — dann gehört „Ändere nichts“ in den Text.
              </p>
            </div>
            <div className="form-actions">
              <button type="button" className="btn btn-secondary" onClick={() => setDialog(null)}>Abbrechen</button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => speichern.mutate(dialog)}
                disabled={!dialogGueltig || speichern.isPending}
              >
                {dialog.id ? 'Speichern' : 'Anlegen'}
              </button>
            </div>
          </>
        )}
      </Modal>
    </div>
  );
}

function VorlageZeile({ v, erste, letzte, beschaeftigt, onHoch, onRunter, onBearbeiten, onStandard, onAktiv, onLoeschen }: {
  v: PromptVorlage; erste: boolean; letzte: boolean; beschaeftigt: boolean;
  onHoch: () => void; onRunter: () => void; onBearbeiten: () => void; onStandard: () => void; onAktiv: () => void; onLoeschen: () => void;
}) {
  return (
    <li className={`pt-vorlage${v.aktiv ? '' : ' pt-vorlage--aus'}`}>
      <div className="pt-vorlage-kopf">
        <strong className="pt-vorlage-name">{v.name}</strong>
        {v.ist_standard && <span className="badge badge-success"><Star size={11} aria-hidden="true" /> Standard</span>}
        {!v.aktiv && <span className="badge badge-muted">ausgeblendet</span>}
        <span className={`badge ${v.aendert_mail ? 'badge-warning' : 'badge-info'}`}>{v.aendert_mail ? 'ändert die Mail' : 'nur prüfen oder lesen'}</span>
      </div>
      {v.beschreibung && <p className="pt-vorlage-beschreibung">{v.beschreibung}</p>}
      <pre className="pt-vorlage-text">{v.anweisungen}</pre>
      <div className="pt-knoepfe">
        <button type="button" className="btn btn-ghost btn-sm" onClick={onHoch} disabled={erste || beschaeftigt} aria-label={`${v.name} nach oben`} title="Nach oben">
          <ArrowUp size={14} aria-hidden="true" />
        </button>
        <button type="button" className="btn btn-ghost btn-sm" onClick={onRunter} disabled={letzte || beschaeftigt} aria-label={`${v.name} nach unten`} title="Nach unten">
          <ArrowDown size={14} aria-hidden="true" />
        </button>
        <button type="button" className="btn btn-secondary btn-sm" onClick={onBearbeiten}><Pencil size={14} aria-hidden="true" /> Bearbeiten</button>
        {!v.ist_standard && (
          <button type="button" className="btn btn-secondary btn-sm" onClick={onStandard} disabled={beschaeftigt}>
            <Star size={14} aria-hidden="true" /> Als Standard
          </button>
        )}
        {!v.ist_standard && (
          <button type="button" className="btn btn-ghost btn-sm" onClick={onAktiv} disabled={beschaeftigt}>
            {v.aktiv ? <><EyeOff size={14} aria-hidden="true" /> Ausblenden</> : <><Eye size={14} aria-hidden="true" /> Einblenden</>}
          </button>
        )}
        {!v.ist_standard && (
          <button type="button" className="btn btn-ghost btn-sm pt-loeschen" onClick={onLoeschen} disabled={beschaeftigt} aria-label={`${v.name} löschen`} title="Löschen">
            <Trash2 size={14} aria-hidden="true" />
          </button>
        )}
      </div>
    </li>
  );
}
